/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectSavedZoomSettings, selectZoomLatLng } from '../../../store';
import { clearSavedZoomSettings, setMapZoomLatLng, setSavedZoomSettings } from '../../../store/maps/actions';

interface MapZoomListenerProps {
  map: google.maps.Map;
  mapId: string;
  persistZoom?: boolean;
}

/**
 * Map listener to zoom the map when a value is provided
 */
function MapZoomListener({ map, mapId, persistZoom }: MapZoomListenerProps) {
  const dispatch = useDispatch();
  const zoom = useSelector(selectZoomLatLng(mapId));
  const zoomSaved = useSelector(selectSavedZoomSettings(mapId));

  React.useEffect(() => {
    if (map && zoomSaved && persistZoom) {
      map.setCenter(zoomSaved.center);
      map.setZoom(zoomSaved.zoom);
      dispatch(clearSavedZoomSettings(mapId));
    }
  }, [dispatch, map, persistZoom, zoomSaved, mapId]);

  React.useEffect(() => {
    return () => {
      if (map && persistZoom) {
        const center = map.getCenter();
        const zoom = map.getZoom();

        if (center && zoom !== undefined) {
          dispatch(setSavedZoomSettings(mapId)(center, zoom));
        }
      }
    };
  }, [dispatch, map, persistZoom, mapId]);

  React.useEffect(() => {
    if (zoom) {
      map.panTo(zoom);
      dispatch(setMapZoomLatLng(mapId)(undefined));
    }
  }, [dispatch, map, zoom, mapId]);

  return null;
}

export default MapZoomListener;
